// Generated by Copilot- Start
import React, { useCallback, useEffect, useMemo, useState } from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "../video-lab/dtv-text";
import { colorPalette } from "@directv-digital/dds.ui.dtv-theme";
import IconBannerV2 from "./components/IconBannerV2/IconBannerV2";
import { APP_BASE_PATH } from "../../utils/app-base-path";
import ChannelModalV2 from "./components/ChannelModalV2";
import MyFreeDtvModalV2 from "./components/MyFreeDtvModalV2/MyFreeDtvModalV2";
import RsnChannelModalV2 from "./components/RsnChannelModalV2";
import LocalChannelModalV2 from "./components/LocalChannelModalV2";
import SvodBannerV2 from "./components/SvodBannerV2";
import SearchResultBanner from "./components/SearchResultBanner";
import MovieModalV2 from "./components/MovieModalV2/MovieModalV2";
import RsnLegalDisclaimer from "./components/RsnLegalDisclaimer";
import { ChlupInteractiveToolV2Props, ChlupInteractiveToolV2ServerData } from "./types";
import {
  ChannelCardV2
} from "lib/helper/channel-interactive-tool-v2";
import ZipcodeModule from "src/components/video-lab/zipcode-module";
import ChannelSearch from "src/components/video-lab/ChannelSearch";
import { useZipcodeHandlers } from "./utils/zipcode-utils";
import { convertLocalRsnToChannelCards, mergeLocalRsnChannels, mergeLocalRsnIntoGenreCategories } from "./utils/local-rsn-utils";
import { useSearchHandlers } from "./utils/search-handlers";
import { useModalHandlers } from "./utils/modal-handlers";
import { mainComponentStyles } from "./styles/component-styles";
import { buildSearchableChannels } from "./utils/searchable-channels-builder";
import { buildLocalChannelLookup, filterDuplicateNationalChannels } from "lib/service/channels/get-local-national-dma-eligible-channels";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";

export const ChlupInteractiveToolV2: React.FC<ChlupInteractiveToolV2Props> = props => {
  try {
    const {
      heading,
      subheading,
      theme,
      backgroundColor,
      enableSvods,
      svodTooltipContent,
      chlupV2,
      id,
      pkgModalContent,
      rowsInDesktop,
      rowsInTablet,
      rowsInMobile,
      maxHeightDesktop,
      maxHeightTablet,
      maxHeightMobile,
      chnlSrc,
      isSearchData,
      genreEnabled,
      zipCodeModule,
      myHomeTeamModalCta,
      tooltips,
      config = {},
      searchObj
    } = props;

    const { openChannelModal, enableZipcodeModule, openSvodModal, enableSearch, enableRadioButton, singlePackageView } = config;

    // State
    const [showMoreCategories, setShowMoreCategories] = useState<Record<string, boolean>>({});

    // Generated by Copilot- Start - 2026-03-30 16:59:31
    /**
     * Lazy-loaded channel data for all variants.
     * The adapter strips all expensive derived channel structures (channelMap,
     * channelsByCategory, allChannels, …) from every SSR payload and caches a
     * fully pre-parsed NormalizedChannelData blob in sharedChannelCache under a
     * variant-specific compound key (chlupV2.cacheVariant).
     *
     * The /api/channel-data route serves that pre-parsed blob verbatim.
     * The component calls setLazyChannelData(data) directly — zero client-side parsing.
     *
     * null = not yet loaded; populated object = loaded and ready.
     */
    const [lazyChannelData, setLazyChannelData] = useState<Pick<
      ChlupInteractiveToolV2ServerData,
      | "channelMap"
      | "channelsByCategory"
      | "addOnChannelsByCategory"
      | "allChannels"
      | "allHiddenChannels"
      | "allAddOnChannels"
    > | null>(null);

    useEffect(() => {
      // All variants lazy-load — channelsLazyLoaded is always true.
      if (!chlupV2?.channelsLazyLoaded) {
        return;
      }

      const controller = new AbortController();
      // WAT-11617: base path comes from the adapter payload ("" in dev, "/uf"
      // deployed). The previous window.__NEXT_DATA__.assetPrefix read was
      // Pages-Router-only: on the App Router that global doesn't exist, the
      // prefix resolved to "" and this fetch 404'd, so the tool never loaded.
      // APP_BASE_PATH backstop (PR review): cached page payloads written by a
      // PRE-fix build have no apiBasePath and survive a deploy in Redis — a bare
      // "" fallback would 404 the fetch (a Pages Router regression) until the
      // refresh cycle rewrites them.
      const prefix = chlupV2?.apiBasePath ?? APP_BASE_PATH;

      // chlupV2.cacheVariant is the compound key the adapter wrote to sharedChannelCache,
      // e.g. "streamclup:genre", "dtvclup:satellite", "streamclup:singlepackage:att_tv_choice".
      // Split at the first colon to get source (for ?source=) and the rest (for ?variant=).
      const cacheVariant = chlupV2?.cacheVariant || "";
      const colonIdx = cacheVariant.indexOf(":");
      const apiSource = encodeURIComponent(colonIdx !== -1 ? cacheVariant.slice(0, colonIdx) : (chnlSrc || "default").toLowerCase());
      const apiVariant = colonIdx !== -1 ? encodeURIComponent(cacheVariant.slice(colonIdx + 1)) : null;
      const apiUrl = `${prefix}/api/channel-data?source=${apiSource}${apiVariant ? `&variant=${apiVariant}` : ""}`;

      const loadChannels = (attempt = 1) => {
        fetch(apiUrl, { signal: controller.signal })
          .then(res => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
          .then((data: Pick<ChlupInteractiveToolV2ServerData, "channelMap" | "channelsByCategory" | "addOnChannelsByCategory" | "allChannels" | "allHiddenChannels" | "allAddOnChannels">) => {
            if (controller.signal.aborted) {
              return;
            }
            // API serves the pre-parsed NormalizedChannelData blob — zero client-side parsing.
            if (data?.channelMap && Object.keys(data.channelMap).length > 0) {
              setLazyChannelData(data);
            }
          })
          .catch(err => {
            if (controller.signal.aborted) {
              return;
            }
            if (attempt < 3) {
              setTimeout(() => loadChannels(attempt + 1), 1000 * attempt);
            } else {
              console.error("[ChlupInteractiveToolV2] Failed to lazy-load channels after 3 attempts:", err);
            }
          });
      };

      loadChannels();
      return () => controller.abort();
    }, [chnlSrc, chlupV2?.channelsLazyLoaded, chlupV2?.cacheVariant]);
    // Generated by Copilot- End - 2026-03-30 16:59:31

    // Resolved channel data — prefers lazy-loaded client data over the (empty) SSR stubs.
    const resolvedChannelMap = lazyChannelData?.channelMap ?? chlupV2?.channelMap ?? {};
    const resolvedChannelsByCategory = lazyChannelData?.channelsByCategory ?? chlupV2?.channelsByCategory ?? {};
    const resolvedAddOnChannelsByCategory = lazyChannelData?.addOnChannelsByCategory ?? chlupV2?.addOnChannelsByCategory ?? {};
    const resolvedAllChannels = lazyChannelData?.allChannels ?? chlupV2?.allChannels ?? [];
    const resolvedAllAddOnChannels = lazyChannelData?.allAddOnChannels ?? chlupV2?.allAddOnChannels ?? [];

    // Custom hook for zipcode handlers - manages its own state
    const { localRsnChnlsData, dmaData, fetchedZipcodeModuleData, ...zipcodeHandlers } = useZipcodeHandlers(chnlSrc);

    // Determine satellite product type for zipcode module config
    const satCQP = chnlSrc === "dtvClup";

    // Shared zipcode handler props — passed to all IconBannerV2 usages for the MYHOMETEAM inline module
    const myhometeamZipcodeProps = {
      onZipcodeSearch: zipcodeHandlers.setLoadingLocalRsnChannels,
      onLocalRsnApiError: zipcodeHandlers.handleLocalRsnApiError,
      onLocalRsnChnlsConfirmed: zipcodeHandlers.addLocalRsnChnls,
      onCacheConfirmed: zipcodeHandlers.onCountySearchDone,
      localRsnApiOptions: {
        satellite: satCQP,
        stream: !satCQP
      },
      zipcodeInitData: fetchedZipcodeModuleData
    };

    const searchHandlers = useSearchHandlers(
      chlupV2?.allCategories,
      props?.showGenrePackages,
      props?.showMyFreeDTVChannels,
      enableSearch,
      resolvedAllAddOnChannels // Pass addon channel names for filtering
    );

    const modalHandlers = useModalHandlers(chlupV2?.packageSummaries, chlupV2?.svodTiles);

    // Merge local/RSN channels into categories when data changes
    const { mergedCategories, mergedChannelsByCategory } = useMemo(() => {
      // If no local/RSN data, return original data
      if (!localRsnChnlsData.localChannels.length && !localRsnChnlsData.rsnChannels.length) {
        return {
          mergedCategories: chlupV2?.bannerCategories || [],
          mergedChannelsByCategory: resolvedChannelsByCategory
        };
      }

      // Genre variant: inject local/RSN channels per genre category bucket + filter duplicate nationals
      if (props.showGenrePackages) {
        const channelsByCategory = mergeLocalRsnIntoGenreCategories(
          localRsnChnlsData,
          chlupV2?.bannerCategories || [],
          chlupV2?.packageSummaries || {},
          props.genreLocalChannelMapping,
          dmaData,
          props.genreNationals,
          resolvedChannelsByCategory,
          resolvedChannelMap // Pass channelMap for denormalization during filtering
        );
        return {
          mergedCategories: chlupV2?.bannerCategories || [],
          mergedChannelsByCategory: channelsByCategory
        };
      }

      // Convert API data to ChannelCardV2 format
      const { localChannels, rsnChannels } = convertLocalRsnToChannelCards(localRsnChnlsData);

      // Merge local/RSN channels into channelsByCategory + filter duplicate nationals
      // Server already provides RSN and LOCAL categories in bannerCategories
      const channelsByCategory = mergeLocalRsnChannels(
        resolvedChannelsByCategory,
        localChannels,
        rsnChannels,
        resolvedChannelMap, // Pass channelMap for denormalization during filtering
        dmaData,
        props.genreLocalChannelMapping,
        props.genreNationals?.genreNationalMapping,
        chlupV2?.packageSummaries
      );

      return {
        mergedCategories: chlupV2?.bannerCategories || [], // Use server categories as-is
        mergedChannelsByCategory: channelsByCategory
      };
    }, [
      localRsnChnlsData,
      chlupV2?.bannerCategories,
      resolvedChannelsByCategory,
      resolvedChannelMap,
      chlupV2?.packageSummaries,
      props.showGenrePackages,
      props.genreLocalChannelMapping,
      props.genreNationals,
      dmaData
    ]);

    // Build filtered allChannels for search (remove duplicate nationals)
    const filteredAllChannels = useMemo(() => {
      // Genre variant doesn't use allChannels (search disabled)
      if (props.showGenrePackages || !resolvedAllChannels.length) {
        return resolvedAllChannels;
      }

      // If no local/RSN data, return original
      if (!localRsnChnlsData.localChannels.length && !localRsnChnlsData.rsnChannels.length) {
        return resolvedAllChannels;
      }

      // Filter duplicate nationals from allChannels
      try {
        const localChannelLookup = buildLocalChannelLookup(localRsnChnlsData.localChannels, localRsnChnlsData.rsnChannels, props.genreNationals?.genreNationalMapping);

        if (localChannelLookup.baseChannelMap.size === 0) {
          return resolvedAllChannels;
        }

        return filterDuplicateNationalChannels(
          resolvedAllChannels,
          localChannelLookup,
          resolvedChannelMap,
          dmaData,
          props.genreLocalChannelMapping,
          props.genreNationals?.genreNationalMapping,
          chlupV2?.packageSummaries
        );
      } catch (error) {
        console.error("Error filtering allChannels for search:", error);
        return resolvedAllChannels;
      }
    }, [
      resolvedAllChannels,
      resolvedChannelMap,
      chlupV2?.packageSummaries,
      localRsnChnlsData,
      props.showGenrePackages,
      props.genreLocalChannelMapping,
      props.genreNationals,
      dmaData
    ]);

    // Build searchable channels list (regular + local + RSN) - uses filtered allChannels
    const searchableChannels = useMemo(
      () => buildSearchableChannels(filteredAllChannels, resolvedChannelMap, localRsnChnlsData),
      [filteredAllChannels, resolvedChannelMap, localRsnChnlsData]
    );

    // [WAT-11791] Refresh the SELECTION whenever the market's channel list changes.
    //
    // `searchableChannels` is already market-scoped (it folds in localRsnChnlsData), so it is the
    // right thing to reconcile against and it changes exactly when the customer changes ZIP. The
    // selection used to survive that untouched: because this tool renders the selected objects
    // DIRECTLY rather than matching them against rows, it went on displaying the previous market's
    // channel — MyNetworkTV in a market that carries no MyNetworkTV — even though the dropdown,
    // rebuilt from the same list, had correctly stopped offering it.
    //
    // The handler holds the same-reference contract, so this cannot loop.
    useEffect(() => {
      searchHandlers.refreshSelectedChannelsForMarket(searchableChannels);
    }, [searchableChannels]);

    // Channel click handler with analytics
    const handleChannelClick = useCallback(
      (channel: ChannelCardV2) => {
        // Check if channel modals are enabled via server config
        if (!openChannelModal) {
          return;
        }

        // RSN and LOCAL channels are special cases - they should always open modals
        // because they're fetched dynamically and might not have package arrays populated.
        // Detected via the structural flags (set at the source); categoryId is kept as a
        // Stage-1 safety net.
        const isRsnOrLocal = Boolean(channel.isRsn || channel.isLocal) || channel.categoryId === "RSN" || channel.categoryId === "LOCAL";

        // Channel should have packages to open modal (except RSN/LOCAL)
        // This includes both "included" and "addon" availability
        const hasPackages = (channel.signaturePackages?.length || 0) > 0 || (channel.genrePackages?.length || 0) > 0;

        if (!hasPackages && !isRsnOrLocal) {
          return;
        }

        modalHandlers.handleChannelClick(channel);
      },
      [openChannelModal, modalHandlers]
    );

    // Show click handler with analytics
    const handleShowClick = useCallback((show: any) => {
      modalHandlers.handleShowClick(show);
    }, []);

    return (
      <DtvBox
        id={id || "ChnlInteractiveTool"}
        sx={{
          ...mainComponentStyles.defaultSpacing,
          ...mainComponentStyles.globalTextColor,
          backgroundColor: backgroundColor || theme?.pageBackgroundColor || "transparent",
          borderRadius: "10px",
          background: theme?.pageBackgroundUrl ? `url(${theme.pageBackgroundUrl}) center / cover no-repeat` : theme?.pageBackgroundColor || ""
        }}
      >
        <DtvBox
          sx={{
            padding: {
 xs: "32px 16.5px",
sm: "48px 64px",
md: "56px 90px"
},
            ...(theme?.category?.mode === "light" && { position: "relative" })
          }}
        >
          {/* Heading */}
          {heading?.content && (
            <DtvBox sx={{
 display: "flex",
justifyContent: "center",
paddingBottom: "0"
}}>
              <DtvText
                component="h2"
                content={heading.content}
                variant="h2"
                sx={{
                  ...mainComponentStyles.heading,
                  color: theme?.headerTextColor || colorPalette.neutral_white.main
                }}
              />
            </DtvBox>
          )}
          {/* Subheading */}
          {subheading?.content && (
            <DtvBox sx={{ marginTop: "9px" }}>
              <DtvText
                component="p"
                content={subheading.content}
                variant="body1"
                sx={{
                  ...mainComponentStyles.subheading,
                  color: theme?.subheadingTextColor || colorPalette.neutral_white.main
                }}
              />
            </DtvBox>
          )}
          {/* Zipcode Module */}
          {enableZipcodeModule && (
            <DtvBox style={{ marginTop: "20px" }}>
              <ZipcodeModule
                ciConfig={zipCodeModule}
                id="chlup-zipcode-module-v2"
                initData={fetchedZipcodeModuleData}
                mode={theme?.zipCodeModuleMode}
                position="center"
                size="medium"
                localRsnApiOptions={{
 satellite: satCQP,
stream: !satCQP
}}
                onCacheConfirmed={zipcodeHandlers.onCountySearchDone}
                onLocalRsnChnlsApiError={zipcodeHandlers.handleLocalRsnApiError}
                onLocalRsnChnlsConfirmed={zipcodeHandlers.addLocalRsnChnls}
                onSearchButtonClick={zipcodeHandlers.setLoadingLocalRsnChannels}
              />
            </DtvBox>
          )}
          {/* Channel Search */}
          {searchHandlers.handleShowSearch() && searchObj && Object.keys(searchObj).length > 0 && (
            <DtvBox sx={{
 maxWidth: "100%",
width: "522px",
margin: "auto",
marginTop: "20px"
}}>
              <ChannelSearch
                id={`${id}-channel-search`}
                {...searchObj}
                // [WAT-11775] Normalised like the compare tool's list. A SvodTileV2 carries
                // `name` and no `displayText`/`value`, so spreading it in raw left every
                // subscription tile invisible to the typeahead.
                channels={[...searchableChannels, ...(chlupV2?.svodTiles ?? []).map((tile: any) => ({
                  ...tile,
                  displayText: tile?.displayText ?? tile?.name,
                  value: tile?.value ?? tile?.name,
                  type: "svod"
                }))]}
                enableRadioButton={enableRadioButton}
                isFFPMultiSearch={props?.showGenrePackages || props?.showMyFreeDTVChannels}
                singlePackageView={singlePackageView}
                theming={theme?.category?.mode}
                type="interactiveTool"
                updateSearchEvents={searchHandlers.updateSearch}
              />
            </DtvBox>
          )}
          {/* RSN Legal Disclaimer */}
          <RsnLegalDisclaimer additionalInfo={props.additionalInfo} showGenrePackages={props.showGenrePackages} theme={theme} />
          {/* Search Results Banner - Shows/Movies grid + Channel/SVOD search results */}
          <SearchResultBanner
            allCategories={chlupV2?.allCategories}
            channelMap={resolvedChannelMap}
            chnlSrc={chnlSrc}
            enableSvods={enableSvods}
            genreEnabled={genreEnabled}
            maxHeightDesktop={maxHeightDesktop}
            maxHeightMobile={maxHeightMobile}
            maxHeightTablet={maxHeightTablet}
            myHomeTeamModalCta={myHomeTeamModalCta}
            myhometeamZipcodeProps={myhometeamZipcodeProps}
            openChannelModal={openChannelModal}
            openSvodModal={openSvodModal}
            pkgModalContent={pkgModalContent}
            rowsInDesktop={rowsInDesktop}
            rowsInMobile={rowsInMobile}
            rowsInTablet={rowsInTablet}
            searchedAddonCategories={searchHandlers.searchedAddonCategories}
            searchedAddonChannelsByCategory={searchHandlers.searchedAddonChannelsByCategory}
            searchedCategories={searchHandlers.searchedCategories}
            searchedChannelsByCategory={searchHandlers.searchedChannelsByCategory}
            selectedChannels={searchHandlers.selectedChannels}
            selectedShows={searchHandlers.selectedShows}
            selectedSvods={searchHandlers.selectedSvods}
            setShowMoreCategories={setShowMoreCategories}
            showMoreCategories={showMoreCategories}
            svodTooltipContent={svodTooltipContent}
            theme={theme}
            tooltips={tooltips}
            zipCodeModule={zipCodeModule}
            onChannelClick={handleChannelClick}
            onShowClick={handleShowClick}
          />
          {/* Regular Channels Container - shown when no search results */}
          {searchHandlers.selectedSvods?.length === 0 && searchHandlers.selectedChannels?.length === 0 && searchHandlers.selectedShows?.length === 0 && (
            <DtvBox
              id="channels-container"
              sx={{
                ...mainComponentStyles.showMoreContainerUtility,
                ...mainComponentStyles.channelsContainer,
                maxHeight: {
                  xs: maxHeightMobile || (rowsInMobile ? `calc(24.8px + 48px + (78.3px * ${rowsInMobile}))` : "307.7px"),
                  sm: maxHeightTablet || (rowsInTablet ? `calc(30px + 21.6px + (105.3px * ${rowsInTablet}))` : "367.5px"),
                  md: maxHeightDesktop || (rowsInDesktop ? `calc(30px + 42px + (109.3px * ${rowsInDesktop}))` : "399.9px")
                }
              }}
            >
              <DtvBox
                id="channel-icons-container"
                sx={{
                  ...mainComponentStyles.channelIconsContainer,
                  color: theme?.headerTextColor || colorPalette.neutral_white.main
                }}
              >
                {enableSvods && chlupV2?.svodTiles && chlupV2.svodTiles.length > 0 && (
                  <SvodBannerV2 chnlSrc={chnlSrc} openSvodModal={openSvodModal} pkgModalContent={pkgModalContent} svods={chlupV2.svodTiles} theme={theme} tooltipContent={svodTooltipContent} />
                )}
                <IconBannerV2
                  addOnChannelsByCategory={resolvedAddOnChannelsByCategory}
                  allCategories={chlupV2?.allCategories || []}
                  categories={mergedCategories}
                  channelMap={resolvedChannelMap}
                  channelsByCategory={mergedChannelsByCategory}
                  genreEnabled={genreEnabled}
                  isSearchData={isSearchData}
                  myHomeTeamModalCta={myHomeTeamModalCta}
                  openChannelModal={openChannelModal}
                  showMoreData={showMoreCategories}
                  theme={theme}
                  tooltips={tooltips}
                  updateShowMoreCategories={setShowMoreCategories}
                  zipCodeModule={zipCodeModule}
                  onChannelClick={handleChannelClick}
                  {...myhometeamZipcodeProps}
                />
              </DtvBox>
            </DtvBox>
          )}
          {/* Modal Routing: MyFreeDTV, RSN, LOCAL, or Regular Channel */}
          {(() => {
            if (!modalHandlers.activeChannel) {
 return null;
}

            // Route to MyFree DTV modal if channel is MyFree
            if (modalHandlers.activeChannel.isMyFreeDTV && chlupV2?.myFreeDTVObj && chnlSrc?.toLowerCase() === "streamclup") {
              return (
                <MyFreeDtvModalV2
                  channel={modalHandlers.activeChannel}
                  myFreeDTVObj={chlupV2.myFreeDTVObj}
                  open={Boolean(modalHandlers.activeChannel)}
                  onClose={modalHandlers.handleChannelClose}
                />
              );
            }

            // Route to RSN modal — detect via the isRsn flag; categoryId is a Stage-1 safety net.
            if (modalHandlers.activeChannel?.isRsn || modalHandlers.activeChannel?.categoryId === "RSN") {
              return (
                <RsnChannelModalV2
                  channel={modalHandlers.activeChannel}
                  chnlSrc={chnlSrc}
                  dmaData={dmaData}
                  open={Boolean(modalHandlers.activeChannel)}
                  packageSummaries={chlupV2?.packageSummaries}
                  pkgModalContent={pkgModalContent}
                  rsnTierLegalText={props.rsnTierLegalTextObj}
                  theme={theme}
                  onClose={modalHandlers.handleChannelClose}
                />
              );
            }

            // Route to LOCAL modal — detect via the isLocal flag; categoryId is a Stage-1 safety net.
            if (modalHandlers.activeChannel?.isLocal || modalHandlers.activeChannel?.categoryId === "LOCAL") {
              return (
                <LocalChannelModalV2
                  channel={modalHandlers.activeChannel}
                  chnlSrc={chnlSrc}
                  dmaData={dmaData}
                  genreLocalChannelMapping={props.genreLocalChannelMapping}
                  open={Boolean(modalHandlers.activeChannel)}
                  packageSummaries={chlupV2?.packageSummaries}
                  pkgModalContent={pkgModalContent}
                  signatureExcludedLocalChannels={props.signatureExcludedLocalChannels}
                  theme={theme}
                  onClose={modalHandlers.handleChannelClose}
                />
              );
            }

            // Route to regular channel modal for all other channels
            return (
              <ChannelModalV2
                addonsLegalText={props.addonsLegalText}
                channel={modalHandlers.activeChannel}
                chnlSrc={chnlSrc}
                displayGenrePack={props.displayGenrePack}
                displayGenrePackSatellite={props.displayGenrePackSatellite}
                open={Boolean(modalHandlers.activeChannel)}
                packageSummaries={chlupV2?.packageSummaries}
                pkgModalContent={pkgModalContent}
                svodPromoMap={chlupV2?.svodPromoMap}
                theme={theme}
                onClose={modalHandlers.handleChannelClose}
              />
            );
          })()}
          {/* Movie/Show Modal */}
          {modalHandlers.clickedShow && (
            <MovieModalV2
              addonsLegalText={props.addonsLegalText}
              channelMap={resolvedChannelMap}
              chnlSrc={chnlSrc}
              clickedShow={modalHandlers.clickedShow}
              genreConfig={props.genreConfig}
              open={Boolean(modalHandlers.clickedShow)}
              ovChnlPageData={chlupV2?.ovChnlPageData || {}}
              packageSummaries={chlupV2?.packageSummaries || {}}
              pkgModalContent={pkgModalContent}
              svodPromoMap={chlupV2?.svodPromoMap}
              theme={theme}
              onClose={modalHandlers.handleShowClose}
            />
          )}
          {/* Backdrop for light mode */}
          {theme?.category?.mode === "light" && searchHandlers.selectedShows?.length < 1 && <DtvBox id="chlup-interactive-tool-backdrop" sx={mainComponentStyles.backdropBox} />}
        </DtvBox>
      </DtvBox>
    );
  } catch (error) {
    console.error("Error in ChlupInteractiveToolV2 >> ", error);
    return <ErrorDisplay component="Channel Interactive Tool V2" error={error} />;
  }
};
// Generated by Copilot- End
